import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

const TurkishLeagueStatsBoard = () => {
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const season = dayjs().year();

    useEffect(() => {
        const fetchStandings = async () => {
            try {
                setLoading(true);
                setError(null);

                const url = `https://site.api.espn.com/apis/site/v2/sports/soccer/tur.1/standings?season=${season}`;
                const res = await fetch(url);

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const data = await res.json();

                if (data.children?.[0]?.standings?.entries) {
                    setStandings(data.children[0].standings.entries);
                } else {
                    setError('No data');
                }
            } catch (err) {
                setError('No data');
            } finally {
                setLoading(false);
            }
        };
        fetchStandings();
    }, [season]);

    if (loading || error || standings.length === 0) {
        return null;
    }

    const getPositionStyle = (position) => {
        if (position <= 4) return 'border-l-2 border-l-emerald-600';
        if (position === 5) return 'border-l-2 border-l-sky-600';
        if (position === 6) return 'border-l-2 border-l-amber-600';
        if (position >= standings.length - 2) return 'border-l-2 border-l-[var(--accent)]';
        return 'border-l-2 border-l-transparent';
    };

    return (
        <div className="bg-[var(--surface)] border border-[var(--line)] p-5 overflow-hidden">
            <h3 className="font-display text-2xl text-[var(--ink)] mb-4">
                Trendyol Süper Lig — Puan Durumu
            </h3>
            <div className="overflow-x-auto border border-[var(--line)]">
                <table className="min-w-full text-sm">
                    <thead className="bg-[var(--bg)] text-[var(--muted)]">
                        <tr>
                            <th className="px-3 py-2.5 text-left font-medium">#</th>
                            <th className="px-3 py-2.5 text-left font-medium min-w-[120px]">Takım</th>
                            <th className="px-3 py-2.5 text-center font-medium">O</th>
                            <th className="px-3 py-2.5 text-center font-medium">G</th>
                            <th className="px-3 py-2.5 text-center font-medium">B</th>
                            <th className="px-3 py-2.5 text-center font-medium">M</th>
                            <th className="px-3 py-2.5 text-center font-semibold text-[var(--ink)]">Puan</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--line)]">
                        {standings.map((entry, i) => {
                            const gp = entry.stats.find((s) => s.name === 'gamesPlayed')?.value ?? 0;
                            const wins = entry.stats.find((s) => s.name === 'wins')?.value ?? 0;
                            const draws = entry.stats.find((s) => s.name === 'ties')?.value ?? 0;
                            const losses = entry.stats.find((s) => s.name === 'losses')?.value ?? 0;
                            const pts = entry.stats.find((s) => s.name === 'points')?.value ?? 0;
                            const logo = entry.team.logos?.[0]?.href;

                            return (
                                <tr
                                    key={entry.team.id}
                                    className={`bg-[var(--surface)] hover:bg-[var(--bg)] transition-colors ${getPositionStyle(i + 1)}`}
                                >
                                    <td className="px-3 py-2.5 font-medium text-[var(--ink)]">{i + 1}</td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            {logo && (
                                                <img
                                                    src={logo}
                                                    alt=""
                                                    className="w-5 h-5 object-contain flex-shrink-0"
                                                />
                                            )}
                                            <span className="font-medium text-[var(--ink)] truncate">
                                                {entry.team.shortDisplayName}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-center text-[var(--muted)]">{gp}</td>
                                    <td className="px-3 py-2.5 text-center text-[var(--ink)]">{wins}</td>
                                    <td className="px-3 py-2.5 text-center text-[var(--muted)]">{draws}</td>
                                    <td className="px-3 py-2.5 text-center text-[var(--muted)]">{losses}</td>
                                    <td className="px-3 py-2.5 text-center font-semibold text-[var(--ink)] tabular-nums">
                                        {pts}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[var(--muted)]">
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-emerald-600" /> Şampiyonlar Ligi
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-sky-600" /> Avrupa Ligi
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-amber-600" /> Konferans Ligi
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-[var(--accent)]" /> Küme Düşme
                </span>
            </div>
        </div>
    );
};

export default TurkishLeagueStatsBoard;
